import { defineComponent as _defineComponent } from 'vue'
import { normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "flex-1 text-xs leading-snug sm:text-sm" }

import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import {
  POLL_INTERVAL_MS,
  envNotice,
  fetchNotice,
  readDismissedId,
  resolveNotice,
  writeDismissedId,
  type OutageNoticeConfig,
  type OutageSeverity,
} from "@/utils/outageNotice";


export default /*#__PURE__*/_defineComponent({
  __name: 'OutageBannerComponent',
  setup(__props) {

const SEVERITY_CLASSES: Record<OutageSeverity, { banner: string; icon: string }> = {
  critical: { banner: "bg-red-700", icon: "text-red-700" },
  warning: { banner: "bg-amber-600", icon: "text-amber-600" },
  info: { banner: "bg-blue-700", icon: "text-blue-700" },
};

const config = ref<OutageNoticeConfig | null>(null);
const dismissedId = ref<string | null>(readDismissedId());

let pollTimer: number | undefined;

const notice = computed(() => resolveNotice(config.value, { dismissedId: dismissedId.value }));

const severityClasses = computed(
  () => SEVERITY_CLASSES[notice.value?.severity ?? "critical"]
);

const refresh = async () => {
  // Falls back to the build-time config when Remote Config cannot be read.
  config.value = (await fetchNotice()) || envNotice();
};

const refreshWhenVisible = () => {
  if (document.visibilityState === "visible") {
    refresh();
  }
};

const dismiss = () => {
  // Read the id before mutating dismissedId: `notice` depends on it, so after
  // the assignment the computed resolves to null.
  const dismissed = notice.value;

  if (!dismissed) {
    return;
  }

  dismissedId.value = dismissed.id;
  writeDismissedId(dismissed.id);
};

onMounted(async () => {
  await refresh();

  // Poll so that switching the notice off clears it from sessions that are
  // already open, and switching it on reaches them without a reload.
  pollTimer = window.setInterval(refresh, POLL_INTERVAL_MS);
  document.addEventListener("visibilitychange", refreshWhenVisible);
});

onBeforeUnmount(() => {
  window.clearInterval(pollTimer);
  document.removeEventListener("visibilitychange", refreshWhenVisible);
});

return (_ctx: any,_cache: any) => {
  return (notice.value)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        role: "alert",
        class: _normalizeClass(["fixed top-0 left-0 right-0 z-[300] flex items-start gap-3 px-4 py-2.5 text-white shadow-md", severityClasses.value.banner])
      }, [
        _createElementVNode("span", {
          class: _normalizeClass(["mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-white text-xs font-bold", severityClasses.value.icon])
        }, "!", 2),
        _createElementVNode("p", _hoisted_1, _toDisplayString(notice.value.message), 1),
        (notice.value.dismissible)
          ? (_openBlock(), _createElementBlock("button", {
              key: 0,
              type: "button",
              "aria-label": "Dismiss notice",
              class: "flex-none px-1 text-xl leading-none text-white",
              onClick: dismiss
            }, " × "))
          : _createCommentVNode("", true)
      ], 2))
    : _createCommentVNode("", true)
}
}

})